// RenderSync (IMPLEMENTATION §2.9 "Views"): the ONLY interpolated motion is a chute carry. PURE.
// A chute holds its item for `hopTicks` ticks (R8): `prog` counts 0…hop; the item enters over the chute's
// back edge (`inDir` side) and leaves over its front edge (`dir`): a straight chute through the cell centre,
// a corner along the bend's centreline (a quarter circle of radius half a cell round
// the cell's inner corner, the same arc as the corner body and its chevrons, art/machines/chute.ts). The
// displayed position is (prog + alpha) / hop along that path, clamped to the front edge (a blocked item
// waits there with prog = hop). When the item hops on, the next chute starts it at prog 0 = its back edge =
// this chute's front edge, so the motion is continuous and monotonic across ticks and chutes.
import type { Dir } from '../sim/core/types.ts';

export interface ChuteGeom {
  x: number;
  y: number;
  /** Out direction (front). */
  dir: Dir;
  /** Back side the items come from. */
  inDir: Dir;
}

const DX: Readonly<Record<Dir, number>> = { N: 0, E: 1, S: 0, W: -1 };
const DY: Readonly<Record<Dir, number>> = { N: -1, E: 0, S: 1, W: 0 };
const OPPOSITE: Readonly<Record<Dir, Dir>> = { N: 'S', E: 'W', S: 'N', W: 'E' };

const QUARTER: Readonly<Record<Dir, number>> = { N: 0, E: 1, S: 2, W: 3 };

/** A corner chute: its entry side is not opposite its exit. */
export function isBent(inDir: Dir, dir: Dir): boolean {
  return inDir !== OPPOSITE[dir];
}

/**
 * How a corner chute shows the one corner chevron frame (canonical entry S, exit E, art/catalog.ts
 * frameId.chevronCorner) - as art chuteTransform places the corner body: mirrored in x for a left turn
 * (S → W), then turned clockwise by `deg`.
 */
export function cornerChevron(inDir: Dir, dir: Dir): { flip: boolean; deg: number } {
  const i = QUARTER[inDir];
  return { flip: (i + 3) % 4 !== QUARTER[dir], deg: ((i + 2) % 4) * 90 };
}

/** Path progress t ∈ [0, 1] of a carry: (prog + alpha) / hop, clamped. */
export function carryT(prog: number, alpha: number, hop: number): number {
  if (hop <= 0) return 1;
  const t = (prog + alpha) / hop;
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

/** World-unit centre of a carry at path progress t (back edge → centre → front edge). */
export function carryWorld(ch: ChuteGeom, t: number, cellWu: number): { x: number; y: number } {
  const cx = (ch.x + 0.5) * cellWu;
  const cy = (ch.y + 0.5) * cellWu;
  const half = cellWu / 2;
  if (isBent(ch.inDir, ch.dir)) {
    // the edges exactly (no trig round-off), so hops between chutes never jump
    if (t <= 0) return { x: cx + DX[ch.inDir] * half, y: cy + DY[ch.inDir] * half };
    if (t >= 1) return { x: cx + DX[ch.dir] * half, y: cy + DY[ch.dir] * half };
    // P = the inner corner; P + v0 = the back edge's midpoint, P + v1 = the front edge's
    const px = cx + (DX[ch.inDir] + DX[ch.dir]) * half;
    const py = cy + (DY[ch.inDir] + DY[ch.dir]) * half;
    const a = (t * Math.PI) / 2;
    const c0 = Math.cos(a);
    const s0 = Math.sin(a);
    return {
      x: px - DX[ch.dir] * half * c0 - DX[ch.inDir] * half * s0,
      y: py - DY[ch.dir] * half * c0 - DY[ch.inDir] * half * s0,
    };
  }
  if (t < 0.5) {
    const k = (0.5 - t) * 2; // 1 at the back edge, 0 at the centre
    return { x: cx + DX[ch.inDir] * half * k, y: cy + DY[ch.inDir] * half * k };
  }
  const k = (t - 0.5) * 2; // 0 at the centre, 1 at the front edge
  return { x: cx + DX[ch.dir] * half * k, y: cy + DY[ch.dir] * half * k };
}
